import { z } from 'zod';
import {
  contactPersonSchema,
  createModel,
  currencySchema,
  legalEntitySchema,
  membersCountSchema,
  partyRoleSchema,
  statusCommonSchema,
} from '../../common';
import { nameSchema } from '../../validation';
import { businessClassificationSchema } from './business.sector.model';
import { businessOrdersSchema } from './business.orders.model';
import { UTagSharedSchema } from '../utag';
import { businessSettingsSchema } from './business.settings.modal';

/**
 * `business db model`
 *
 * @deprecated **Relations-v2 transition** — the legacy `Business` and
 * `Carrier` collections are being unified into the new `Org` collection
 * (Phase 3 dual-write begins; legacy collections removed per the
 * future-cleanup roadmap). Kept here so existing readers and tooling
 * keep working during Phases 1–6. NEW writes SHOULD go through the
 * `Org` collection and the `Party` builder family
 * (see `PartyResolverService.makeParty`).
 *
 * Removal is scheduled in
 * {@link file://./../../../../../../docs/relations-v2/05-future-cleanup-roadmap.md `docs/relations-v2/05-future-cleanup-roadmap.md`}.
 * See also `docs/relations-v2/07-collection-unification.md` for the
 * Business+Carrier → Org migration plan.
 */
export const BusinessSchema = createModel({
  /** the status of the business */
  status: statusCommonSchema,
  /** the name of the business to display */
  name: nameSchema,
  /** business category and sector */
  classification: businessClassificationSchema.optional(),
  /** expected orders for the business to generate on a monthly basis */
  orders: businessOrdersSchema,
  /** the primary contact of the business. this will automaticlyy be the person who signed up.
   *
   * this can be changed later on any time
   */
  primaryContact: contactPersonSchema,
  /** company legal info */
  legal: legalEntitySchema.optional(),
  /** the currency in which the business will have its money in */
  currency: currencySchema,
  /** a count of any member related  to the business such as dispatchers / employees / managers */
  members: membersCountSchema,
  UTags: z.array(UTagSharedSchema),
  /** the settings of the business */
  settings: businessSettingsSchema.optional(),
  /**
   * the network roles this Business plays. Defaults to `['customer']`
   * during the transition; multi-role Orgs (customer + carrier + broker)
   * are tracked on the new `Org` collection — see
   * `docs/relations-v2/07-collection-unification.md`. ≥ 1.
   */
  roles: z.array(partyRoleSchema).min(1),
});
/**
 * business db model
 *
 * @deprecated See {@link BusinessSchema}. Use `Org` for new code.
 */
export type BusinessDoc = z.infer<typeof BusinessSchema.model>;
/** @deprecated Use Org / {@link BusinessDoc}. */
export type { BusinessDoc as LegacyBusiness };
/** @deprecated See {@link BusinessSchema}. Use `Org` for new code. */
export type BusinessData = z.infer<typeof BusinessSchema.data>;
/** @deprecated See {@link BusinessSchema}. Use `Org` for new code. */
export type BusinessPartial = z.infer<typeof BusinessSchema.partial>;
